import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore, useUIStore, useAlertStore, ROLES } from '../../store'
import {
  LayoutDashboard,
  Users,
  Scissors,
  Building2,
  FileText,
  Upload,
  Bell,
  Settings,
  LogOut,
  Menu,
  X
} from 'lucide-react'
import clsx from 'clsx'
import { CentinelaLogoIcon } from '../CentinelaLogo'

// Definir qué roles pueden ver cada módulo
// Si no tiene 'roles', todos pueden verlo excepto hospital_user (que tiene acceso muy limitado)
const allNavItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: [ROLES.ADMIN, ROLES.OPERADOR] },
  { path: '/pacientes', icon: Users, label: 'Pacientes', roles: [ROLES.ADMIN, ROLES.OPERADOR] },
  { path: '/cirugias', icon: Scissors, label: 'Cirugias', roles: [ROLES.ADMIN, ROLES.OPERADOR] },
  { path: '/hospitales', icon: Building2, label: 'Hospitales', roles: [ROLES.ADMIN, ROLES.OPERADOR] },
  { path: '/reportes', icon: FileText, label: 'Reportes', roles: [ROLES.ADMIN, ROLES.OPERADOR] },
  { path: '/upload', icon: Upload, label: 'Carga de Datos', roles: [ROLES.ADMIN, ROLES.HOSPITAL_USER] },
  { path: '/alertas', icon: Bell, label: 'Alertas', roles: [ROLES.ADMIN, ROLES.OPERADOR] },
  { path: '/configuracion', icon: Settings, label: 'Configuracion', roles: [ROLES.ADMIN] },
]

export default function MainLayout() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const unreadCount = useAlertStore(state => state.unreadCount)

  // Filtrar items de navegación según rol del usuario
  const userRole = user?.rol
  const navItems = allNavItems.filter(item => {
    // Si el item tiene roles definidos, verificar que el usuario tenga uno de esos roles
    if (item.roles) {
      return item.roles.includes(userRole)
    }
    return true
  })

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 bg-green-800 text-white transition-transform duration-300 ease-in-out',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex items-center justify-between h-20 px-4 bg-green-900">
          <div className="flex items-center gap-3">
            <CentinelaLogoIcon className="h-12 w-12" />
            <div className="leading-tight">
              <h1 className="text-lg font-bold">Quirofanos</h1>
              <span className="text-xs text-green-300">Sistema de Gestion</span>
            </div>
          </div>
          <button onClick={toggleSidebar} className="lg:hidden">
            <X size={24} />
          </button>
        </div>

        <nav className="mt-4">
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) => clsx(
                'flex items-center gap-3 px-4 py-3 hover:bg-green-700 transition-colors',
                isActive && 'bg-green-700 border-r-4 border-white'
              )}
            >
              <Icon size={20} />
              <span>{label}</span>
              {path === '/alertas' && unreadCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-green-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              {user?.nombre?.[0] || 'U'}
            </div>
            <div>
              <p className="font-medium">{user?.nombre || 'Usuario'}</p>
              <p className="text-sm text-green-300">{user?.rol || 'Admin'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 bg-green-700 hover:bg-green-600 rounded transition-colors"
          >
            <LogOut size={18} />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={clsx(
        'transition-all duration-300',
        sidebarOpen ? 'lg:ml-64' : 'ml-0'
      )}>
        {/* Top bar */}
        <header className="bg-white shadow-sm h-16 flex items-center px-4">
          <button onClick={toggleSidebar} className="mr-4">
            <Menu size={24} />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-4">
            {/* Solo mostrar alertas si el usuario tiene acceso a ese módulo */}
            {userRole !== ROLES.HOSPITAL_USER && (
              <NavLink to="/alertas" className="relative">
                <Bell size={24} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </NavLink>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
